import MiniSearch from 'minisearch';

export type Channel = {
  id: string;
  name: string;
  country: string;
  countryCode: string;
  category: string;
  logo: string;
  tvgId?: string;
  url: string;
  broadcasterGroup?: 'world-cup' | 'top-sports' | null;
};

export type Country = {
  code: string;
  name: string;
  flag: string;
  count: number;
};

export type Category = {
  id: string;
  name: string;
  count: number;
};

export type SportSubcategory = {
  id: string;
  label: string;
  icon: string;
  keywords: string[];
};

export const SPORT_SUBCATEGORIES: SportSubcategory[] = [
  { id: 'football', label: 'Football', icon: '⚽', keywords: ['football', 'soccer'] },
  { id: 'basketball', label: 'Basketball', icon: '🏀', keywords: ['basketball', 'nba'] },
  { id: 'cricket', label: 'Cricket', icon: '🏏', keywords: ['cricket'] },
  { id: 'tennis', label: 'Tennis', icon: '🎾', keywords: ['tennis'] },
  {
    id: 'motorsport',
    label: 'Motorsport',
    icon: '🏎️',
    keywords: ['motorsport', 'f1', 'moto', 'racing', 'formula'],
  },
  {
    id: 'combat',
    label: 'Combat Sports',
    icon: '🥊',
    keywords: ['boxing', 'mma', 'ufc', 'wrestling', 'combat', 'fight'],
  },
  { id: 'esports', label: 'Esports', icon: '🎮', keywords: ['esports', 'gaming'] },
];

export const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States', GB: 'United Kingdom', CA: 'Canada', AU: 'Australia',
  IN: 'India', DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain',
  PT: 'Portugal', NL: 'Netherlands', BE: 'Belgium', AT: 'Austria', CH: 'Switzerland',
  IE: 'Ireland', SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland',
  IS: 'Iceland', PL: 'Poland', CZ: 'Czech Republic', SK: 'Slovakia', HU: 'Hungary',
  RO: 'Romania', BG: 'Bulgaria', GR: 'Greece', TR: 'Turkey', RU: 'Russia',
  UA: 'Ukraine', BR: 'Brazil', AR: 'Argentina', MX: 'Mexico', CL: 'Chile',
  CO: 'Colombia', PE: 'Peru', VE: 'Venezuela', UY: 'Uruguay', PY: 'Paraguay',
  BO: 'Bolivia', EC: 'Ecuador', CR: 'Costa Rica', PA: 'Panama', DO: 'Dominican Republic',
  CU: 'Cuba', PR: 'Puerto Rico', JM: 'Jamaica', TT: 'Trinidad and Tobago',
  CN: 'China', JP: 'Japan', KR: 'South Korea', KP: 'North Korea', TW: 'Taiwan',
  HK: 'Hong Kong', SG: 'Singapore', MY: 'Malaysia', TH: 'Thailand', VN: 'Vietnam',
  PH: 'Philippines', ID: 'Indonesia', KH: 'Cambodia', LA: 'Laos', MM: 'Myanmar',
  BD: 'Bangladesh', PK: 'Pakistan', LK: 'Sri Lanka', NP: 'Nepal', AF: 'Afghanistan',
  IR: 'Iran', IQ: 'Iraq', SA: 'Saudi Arabia', AE: 'United Arab Emirates',
  QA: 'Qatar', KW: 'Kuwait', BH: 'Bahrain', OM: 'Oman', YE: 'Yemen',
  JO: 'Jordan', LB: 'Lebanon', SY: 'Syria', IL: 'Israel', PS: 'Palestine',
  EG: 'Egypt', LY: 'Libya', TN: 'Tunisia', DZ: 'Algeria', MA: 'Morocco',
  ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya', GH: 'Ghana', ET: 'Ethiopia',
  TZ: 'Tanzania', UG: 'Uganda', ZM: 'Zambia', ZW: 'Zimbabwe', AO: 'Angola',
  MZ: 'Mozambique', SD: 'Sudan', SN: 'Senegal', CI: "Côte d'Ivoire", CM: 'Cameroon',
  NZ: 'New Zealand', FJ: 'Fiji', PG: 'Papua New Guinea',
};

export const FEATURED_CHANNELS = {
  worldCup: [
    'FIFAPlus.uk@English',
    'FIFAPlus.uk@French',
    'FIFAPlus.uk@German',
    'FIFAPlus.uk@HispanicAmerica',
    'FIFAPlus.uk@Italy',
    'FIFAPlus.uk@Portuguese',
    'FIFAPlus.uk@Spain',
    'FIFAPlus.uk@UnitedStates',
    'FIFAPlusWomen.uk@English',
    'FoxSports1.us@HD',
    'FoxDeportes.us@SD',
    'TelemundoInternacional.us@South',
    'CazeTV.br@SD',
    'RedeGlobo.br@SD',
    'TyCSports.ar@SD',
    'WinSports.co@SD',
    'Teledeporte.es@SD',
    'SportTV1.pt@SD',
    'Sportitalia.it@SD',
    'TVPSport.pl@SD',
    'PolsatSportPremium1.pl@SD',
    'AntenaSport.ro@SD',
    'PTVSports.pk@SD',
    'TenSportsPakistan.pk@SD',
    'TRTSpor.tr@SD',
    'TSports.bd@SD',
    'StarSports1.in@HD',
  ],
  topSports: [
    'FoxDeportes.us@SD',
    'ESPNDeportes.us@SD',
    'CBSSportsGolazoNetwork.us@SD',
    'CBSSportsHQ.us@SD',
    'ESPN8TheOcho.us@ESPN8TheOchoHD',
    'beINSPORTSXTRA.us@SD',
    'beINSPORTSXTRAenEspanol.us@SD',
    'FanDuelRacing.us@SD',
    'Combate.br@SD',
    'ACISportTV.it@SD',
    'DSportsUruguay.uy@SD',
  ],
};

export function matchesSubcategory(channel: Channel, subcategoryId: string): boolean {
  const sub = SPORT_SUBCATEGORIES.find((s) => s.id === subcategoryId);
  if (!sub) return false;
  const lower = channel.category.toLowerCase();
  return sub.keywords.some((k) => lower.includes(k));
}

export function isCJK(s: string): boolean {
  return /[\u3000-\u9fff\uff00-\uffef]/.test(s);
}

export function sortChannelsByRelevance(channels: Channel[]): Channel[] {
  return [...channels].sort((a, b) => {
    const aCjk = isCJK(a.name);
    const bCjk = isCJK(b.name);
    if (aCjk !== bCjk) return aCjk ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
}

export function buildSearchIndex(channels: Channel[]): MiniSearch<Channel> {
  const index = new MiniSearch<Channel>({
    fields: ['name', 'country', 'category', 'id'],
    storeFields: ['id'],
    searchOptions: {
      boost: { name: 3, country: 2, id: 1.5 },
      fuzzy: 0.2,
      prefix: true,
      combineWith: 'AND',
    },
  });
  index.addAll(channels);
  return index;
}

export function searchChannels(
  index: MiniSearch<Channel>,
  channels: Channel[],
  query: string,
  limit = 50
): Channel[] {
  if (!query.trim()) return [];
  const results = index.search(query, { fuzzy: 0.2, prefix: true });
  const idSet = new Set(results.slice(0, limit).map((r) => r.id));
  const orderMap = new Map(results.map((r, i) => [r.id, i]));
  return channels
    .filter((c) => idSet.has(c.id))
    .sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
}

export function countryCodeToFlag(code: string): string {
  if (!code || code.length !== 2) return '🏳️';
  const A = 0x1f1e6;
  const a = 'A'.charCodeAt(0);
  return String.fromCodePoint(
    A + code.charCodeAt(0) - a,
    A + code.charCodeAt(1) - a
  );
}

export function applyFeaturedTags(channels: Channel[]): Channel[] {
  const worldCupIds = new Set(FEATURED_CHANNELS.worldCup);
  const topSportsIds = new Set(FEATURED_CHANNELS.topSports);
  return channels.map((ch) => {
    if (worldCupIds.has(ch.id)) return { ...ch, broadcasterGroup: 'world-cup' };
    if (topSportsIds.has(ch.id)) return { ...ch, broadcasterGroup: 'top-sports' };
    return ch;
  });
}

export function extractCountries(channels: Channel[]): Country[] {
  const map = new Map<string, Country>();
  for (const ch of channels) {
    const existing = map.get(ch.countryCode);
    if (existing) {
      existing.count++;
    } else {
      map.set(ch.countryCode, {
        code: ch.countryCode,
        name: ch.country,
        flag: countryCodeToFlag(ch.countryCode),
        count: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function extractCategories(channels: Channel[]): Category[] {
  const map = new Map<string, { id: string; name: string; count: number }>();
  for (const ch of channels) {
    const existing = map.get(ch.category);
    if (existing) {
      existing.count++;
    } else {
      map.set(ch.category, { id: ch.category, name: ch.category, count: 1 });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}
